import type { AudioPlayerContextValue } from '@ibsys/kit';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { EditedInterval } from './types';

/**
 * Насколько позиция записи может уйти за границы отрезка, пока он ещё прослушивается, с.
 * Дальше считается, что запись перемотали сами, и останавливать её на конце отрезка уже не нужно.
 */
const INTERVAL_SEEK_TOLERANCE_SEC = 0.5;

/**
 * Прослушивание отрезка записи: перемотка на его начало, воспроизведение и остановка на конце.
 * Отрезок перестаёт прослушиваться, когда воспроизведение остановилось или запись перемотали за его пределы.
 */
export const useIntervalPlayback = ({ state, actions }: AudioPlayerContextValue) => {
  const [playingInterval, setPlayingInterval] = useState<EditedInterval | null>(null);
  // Воспроизведение отрезка уже шло: его остановка — конец прослушивания, а не ожидание запуска
  const hasStartedRef = useRef(false);
  const { isPlaying, currentTime, rate } = state;
  const { seek, play, pause } = actions;

  const playInterval = useCallback(
    (interval: EditedInterval) => {
      hasStartedRef.current = false;
      setPlayingInterval(interval);
      // Границы — в миллисекундах, плеер принимает секунды
      seek(interval.startedAt / 1000);
      void play();
    },
    [seek, play],
  );

  useEffect(() => {
    if (!playingInterval) return;
    if (!isPlaying) {
      if (hasStartedRef.current) setPlayingInterval(null);
      return;
    }
    hasStartedRef.current = true;

    const startSec = playingInterval.startedAt / 1000;
    const endSec = playingInterval.finishedAt / 1000;
    if (currentTime < startSec - INTERVAL_SEEK_TOLERANCE_SEC || currentTime > endSec + INTERVAL_SEEK_TOLERANCE_SEC) {
      setPlayingInterval(null);
      return;
    }
    if (currentTime >= endSec) {
      pause();
      return;
    }
    // Позиция приходит с шагом в доли секунды — конец отрезка ловится таймером от последней позиции.
    // Пока запись не сдвинулась с начала (ещё подгружается), таймер не ставится: он отмерил бы отрезок
    // раньше, чем зазвучит запись
    if (currentTime <= startSec) return;
    const timer = setTimeout(pause, ((endSec - currentTime) / rate) * 1000);
    return () => clearTimeout(timer);
  }, [playingInterval, isPlaying, currentTime, rate, pause]);

  return { playingInterval, playInterval };
};
